import { useCallback, useEffect, useRef } from "react";
import { useEditorStore } from "../../state/editorStore";

/**
 * Keeps Zustand object-tool state and CanvasEditor ref in sync (single editor only).
 * Store is source of truth for panel changes; canvas handle rotation writes back on release.
 * @param {React.RefObject} canvasEditorRef
 * @param {unknown} syncKey — re-apply when canvas image/session changes (e.g. processedImage URL)
 */
export function useCanvasToolSync(canvasEditorRef, syncKey = null) {
  const object = useEditorStore((s) => s.object);
  const updateObject = useEditorStore((s) => s.updateObject);
  const lastAppliedRef = useRef("");

  // Store → canvas
  useEffect(() => {
    const ref = canvasEditorRef.current;
    if (!ref) return;

    const { shadow, outline } = object;
    const signature = [
      object.rotation,
      object.flipH,
      object.flipV,
      shadow?.enabled,
      shadow?.x,
      shadow?.y,
      shadow?.blur,
      shadow?.opacity,
      shadow?.color,
      outline?.enabled,
      outline?.width,
      outline?.blur,
      outline?.color,
      syncKey,
    ].join("|");

    if (lastAppliedRef.current === signature) {
      return;
    }
    lastAppliedRef.current = signature;

    const canvasRotation = ref.getRotation?.() ?? 0;
    const canvasFlip = ref.getFlip?.() ?? { flipH: false, flipV: false };

    if (object.rotation !== canvasRotation) {
      ref.setRotation(object.rotation);
    }
    if (
      object.flipH !== canvasFlip.flipH ||
      object.flipV !== canvasFlip.flipV
    ) {
      ref.setFlip(object.flipH, object.flipV);
    }

    ref.setShadowEnabled?.(shadow.enabled);
    ref.setShadowOffset?.(shadow.x, shadow.y);
    ref.setShadowBlur?.(shadow.blur);
    ref.setShadowOpacity?.(shadow.opacity);
    ref.setShadowColor?.(shadow.color);

    ref.setOutlineEnabled?.(outline.enabled);
    ref.setOutlineWidth?.(outline.width);
    ref.setOutlineBlur?.(outline.blur);
    ref.setOutlineColor?.(outline.color);
  }, [object, canvasEditorRef, syncKey]);

  const onObjectTransformChange = useCallback(
    (transform) => {
      const current = useEditorStore.getState().object;
      if (
        transform.rotation != null &&
        transform.rotation !== current.rotation
      ) {
        updateObject("rotation", transform.rotation, { recordHistory: false });
      }
      if (
        transform.flipH != null &&
        transform.flipH !== current.flipH
      ) {
        updateObject("flipH", transform.flipH, { recordHistory: false });
      }
      if (
        transform.flipV != null &&
        transform.flipV !== current.flipV
      ) {
        updateObject("flipV", transform.flipV, { recordHistory: false });
      }
    },
    [updateObject]
  );

  return onObjectTransformChange;
}
